import type { StudentActiveCohort, StudentCohortAssignmentReleased } from "../auth/api-client";
import { getStudentReleasedAssignments } from "../auth/api-client";
import { parseApiInstant } from "../datetime/parseApiInstant";
import { deriveStudentDisplayStatus } from "./studentDisplayStatus";

export function computeCohortProgressStats(assignments: StudentCohortAssignmentReleased[]) {
  const released = assignments.filter((a) => a.released);
  const complete = released.filter((a) => deriveStudentDisplayStatus(a) === "complete").length;
  const open = released.filter((a) => a.open).length;
  return { total: released.length, complete, open };
}

export function findLatestIncomplete(
  assignments: StudentCohortAssignmentReleased[]
): StudentCohortAssignmentReleased | null {
  const openReleased = assignments.filter((a) => a.open);
  const incomplete = openReleased.filter((a) => deriveStudentDisplayStatus(a) !== "complete");
  if (incomplete.length === 0) return null;
  const withDue = incomplete.filter((a) => a.dueAt);
  if (withDue.length > 0) {
    return (
      [...withDue].sort(
        (a, b) => (parseApiInstant(a.dueAt)?.getTime() ?? 0) - (parseApiInstant(b.dueAt)?.getTime() ?? 0)
      )[0] ?? null
    );
  }
  return incomplete[0] ?? null;
}

export interface OtherCohortIncomplete {
  cohort: StudentActiveCohort;
  assignment: StudentCohortAssignmentReleased;
}

/** When caught up in the current cohort, find open incomplete work in another enrolled cohort. */
export async function findOtherCohortWithIncomplete(
  bearer: string,
  cohorts: StudentActiveCohort[],
  currentCohortId: string
): Promise<OtherCohortIncomplete | null> {
  const others = cohorts.filter((c) => c.cohortId !== currentCohortId);
  if (others.length === 0) return null;

  const results = await Promise.all(
    others.map(async (cohort) => {
      try {
        const data = await getStudentReleasedAssignments(bearer, cohort.cohortId);
        const next = findLatestIncomplete(data.assignments);
        return next ? { cohort, assignment: next } : null;
      } catch {
        return null;
      }
    })
  );

  return results.find((r): r is OtherCohortIncomplete => r !== null) ?? null;
}
